<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">
                        Doctor Available Day Details
                      </h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        {{ isEditMode ? "Modify doctor availability information" : "View doctor availability details" }}
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                      :loading="loading"
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to List
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Loading State -->
      <v-row v-if="initialLoading">
        <v-col cols="12">
          <v-card elevation="3" style="height: 400px;">
            <div class="d-flex align-center justify-center h-100">
              <div class="text-center">
                <health-loader message="Loading available day details..." />
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row v-else>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Available Day Information</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>
                  {{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}
                </v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>

            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2">
              <v-form ref="form" v-model="valid" lazy-validation>
                
                <!-- Basic Information Section -->
                <div class="form-section mb-2">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-calendar-check</v-icon>
                    Availability Information
                  </h3>
                  
                  <v-row>
                    <!-- User Selection -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.user_id"
                        :items="users"
                        label="User"
                        placeholder="Select a user"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'User selection is required']"
                        :error-messages="errors.user_id"
                        class="professional-field"
                        hide-details="auto"
                        :loading="loadingUsers"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-avatar>
                            <v-icon>mdi-account</v-icon>
                          </v-list-item-avatar>
                          <v-list-item-content>
                            <v-list-item-title>{{ item.text }}</v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <!-- Day Selection -->
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="form.day"
                        :items="days"
                        label="Day"
                        placeholder="Select day of the week"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => !!v || 'Day selection is required']"
                        :error-messages="errors.day"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>
                              <v-chip 
                                :color="getDayColor(item.value)"
                                outlined
                                x-small
                                class="mr-2"
                              >
                                {{ item.text }}
                              </v-chip>
                              <span class="grey--text">
                                ({{ getDayType(item.value) }})
                              </span>
                            </v-list-item-title>
                          </v-list-item-content>
                        </template>
                        <template v-slot:selection="{ item }">
                          <v-chip 
                            :color="getDayColor(item.value)"
                            outlined
                            small
                          >
                            <v-icon left x-small>mdi-calendar</v-icon>
                            {{ item.text }}
                          </v-chip>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row>
                    <!-- Availability Status -->
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.is_available"
                        :items="availabilityOptions"
                        label="Availability Status"
                        placeholder="Select availability status"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => v !== null && v !== undefined || 'Availability status is required']"
                        :error-messages="errors.is_available"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-check-circle</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>
                              <v-chip 
                                :color="item.value ? 'success' : 'error'"
                                outlined
                                x-small
                                class="mr-2"
                              >
                                <v-icon left x-small>
                                  {{ item.value ? 'mdi-check' : 'mdi-close' }}
                                </v-icon>
                                {{ item.text }}
                              </v-chip>
                            </v-list-item-title>
                          </v-list-item-content>
                        </template>
                        <template v-slot:selection="{ item }">
                          <v-chip 
                            :color="item.value ? 'success' : 'error'"
                            outlined
                            small
                          >
                            <v-icon left x-small>
                              {{ item.value ? 'mdi-check' : 'mdi-close' }}
                            </v-icon>
                            {{ item.text }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>

                    <!-- Status -->
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="form.is_active"
                        :items="statusOptions"
                        label="Status"
                        placeholder="Select status"
                        outlined
                        :disabled="!isEditMode"
                        :readonly="!isEditMode"
                        :rules="[(v) => v !== null && v !== undefined || 'Status is required']"
                        :error-messages="errors.is_active"
                        class="professional-field"
                        hide-details="auto"
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-toggle-switch</v-icon>
                        </template>
                        <template v-slot:item="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>
                              <v-chip 
                                :color="item.value ? 'success' : 'warning'"
                                outlined
                                x-small
                                class="mr-2"
                              >
                                <v-icon left x-small>
                                  {{ item.value ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                                </v-icon>
                                {{ item.text }}
                              </v-chip>
                            </v-list-item-title>
                          </v-list-item-content>
                        </template>
                        <template v-slot:selection="{ item }">
                          <v-chip 
                            :color="item.value ? 'success' : 'warning'"
                            outlined
                            small
                          >
                            <v-icon left x-small>
                              {{ item.value ? 'mdi-check-circle' : 'mdi-pause-circle' }}
                            </v-icon>
                            {{ item.text }}
                          </v-chip>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </div>

                <!-- Metadata Section -->
                <div class="form-section mb-2" v-if="data">
                  <h3 class="section-title mb-1">
                    <v-icon color="primary" class="mr-2">mdi-information</v-icon>
                    Record Information
                  </h3>
                  
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="data.created_by?.user_name || 'N/A'"
                        label="Created By"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-account-plus</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="formatDate(data.created_at)"
                        label="Created At"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-plus</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="formatDate(data.updated_at)"
                        label="Last Updated"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-calendar-edit</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-text-field
                        :value="data.company?.name || 'N/A'"
                        label="Company"
                        outlined
                        readonly
                        class="professional-field"
                        hide-details
                      >
                        <template v-slot:prepend-inner>
                          <v-icon color="grey">mdi-domain</v-icon>
                        </template>
                      </v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Information Card -->
                <v-card 
                  color="blue lighten-5" 
                  outlined 
                  class="mb-4"
                  v-if="form.user_id && form.day && data"
                >
                  <v-card-text>
                    <div class="d-flex align-center">
                      <v-icon color="info" class="mr-2">mdi-information</v-icon>
                      <div>
                        <div class="text-body-2 font-weight-medium mb-1">
                          Current Settings for {{ getSelectedUserName() }}
                        </div>
                        <div class="text-caption grey--text">
                          Day: <strong>{{ form.day }}</strong> 
                          ({{ getDayType(form.day) }})
                          • Status: 
                          <strong :class="form.is_available ? 'success--text' : 'error--text'">
                            {{ form.is_available ? 'Available' : 'Not Available' }}
                          </strong>
                          • Record Status: 
                          <strong :class="form.is_active ? 'success--text' : 'warning--text'">
                            {{ form.is_active ? 'Active' : 'Inactive' }}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>

                <!-- Action Buttons (only in edit mode) -->
                <div v-if="isEditMode" class="form-actions mt-6">
                  <v-row>
                    <v-col cols="12" class="d-flex justify-end align-center">
                      <!-- Cancel Button -->
                      <v-btn
                        color="grey darken-1"
                        outlined
                        large
                        @click="cancelEdit"
                        class="mr-3 action-button-cancel"
                        :disabled="loading"
                      >
                        <v-icon left>mdi-close</v-icon>
                        Cancel
                      </v-btn>

                      <!-- Update Button -->
                      <v-btn
                        color="primary"
                        large
                        @click="updateRecord"
                        class="action-button-submit"
                        :loading="loading"
                        :disabled="!valid || loading"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        {{ loading ? "Updating..." : "Update Available Day" }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
export default {
  name: "DoctorAvailableDayDetails",
  data() {
    return {
      valid: true,
      loading: false,
      initialLoading: true,
      loadingUsers: false,
      isEditMode: false,
      
      // Data
      data: null,
      originalData: null,
      
      // Form data
      form: {
        user_id: null,
        day: null,
        is_available: true,
        is_active: true,
      },
      
      // Validation errors
      errors: {},
      
      // Dropdown options
      users: [],
      days: [
        { text: "Monday", value: "Monday" },
        { text: "Tuesday", value: "Tuesday" },
        { text: "Wednesday", value: "Wednesday" },
        { text: "Thursday", value: "Thursday" },
        { text: "Friday", value: "Friday" },
        { text: "Saturday", value: "Saturday" },
        { text: "Sunday", value: "Sunday" },
      ],
      availabilityOptions: [
        { text: "Available", value: true },
        { text: "Not Available", value: false },
      ],
      statusOptions: [
        { text: "Active", value: true },
        { text: "Inactive", value: false },
      ],
    };
  },
  mounted() {
    this.getData();
    this.getUsers();
  },
  methods: {
    // Data fetching
    async getData() {
      this.initialLoading = true;
      try {
        const response = await this.$http.get(`/doctor_available_days/${this.$route.params.id}`);
        this.data = response.data.data;
        this.setFormData();
      } catch (error) {
        console.error("Error fetching doctor available day:", error);
        this.$store.dispatch("errors/handleError", error);
        
        if (error.response && error.response.status === 404) {
          this.$router.push("/doctor-available-days");
        }
      } finally {
        this.initialLoading = false;
      }
    },

    async getUsers() {
      this.loadingUsers = true;
      try {
        const response = await this.$http.get("/doctor_available_days/dropdown");
        this.users = (response.data.data || []).map(user => ({
          text: `${user.name} (${user.email})`,
          value: user.id,
          name: user.name,
          email: user.email,
        }));
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        this.loadingUsers = false;
      }
    },

    // Form management
    setFormData() {
      if (this.data) {
        this.form = {
          user_id: this.data.user_id,
          day: this.data.day,
          is_available: this.data.is_available,
          is_active: this.data.is_active,
        };
        this.originalData = { ...this.form };
      }
    },

    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      if (!this.isEditMode) {
        // Reset form to original data when exiting edit mode
        this.form = { ...this.originalData };
        this.errors = {};
      }
    },

    cancelEdit() {
      this.form = { ...this.originalData };
      this.errors = {};
      this.isEditMode = false;
    },

    // Helper methods
    getDayColor(day) {
      const colors = {
        Monday: "blue",
        Tuesday: "green", 
        Wednesday: "orange",
        Thursday: "purple",
        Friday: "teal",
        Saturday: "red",
        Sunday: "indigo",
      };
      return colors[day] || "grey";
    },

    getDayType(day) {
      const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
      return weekdays.includes(day) ? "Weekday" : "Weekend";
    },

    getSelectedUserName() {
      const selectedUser = this.users.find(user => user.value === this.form.user_id);
      return selectedUser ? selectedUser.name : "Selected User";
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleString();
    },

    // Update method
    async updateRecord() {
      // Validate form
      if (!this.$refs.form.validate()) {
        this.$store.dispatch("errors/showError", "Please fill in all required fields correctly");
        return;
      }

      this.loading = true;
      this.errors = {};

      try {
        const response = await this.$http.patch(`/doctor_available_days/${this.$route.params.id}`, this.form);
        
        this.$store.dispatch("errors/showSuccess", response.data.message || "Doctor available day updated successfully");
        
        // Refresh data and exit edit mode
        await this.getData();
        this.isEditMode = false;
      } catch (error) {
        console.error("Error updating doctor available day:", error);
        
        if (error.response && error.response.status === 422) {
          // Validation errors
          this.errors = error.response.data.errors || {};
          this.$store.dispatch("errors/showError", "Please check the form for errors");
        } else {
          this.$store.dispatch("errors/handleError", error);
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
/* Professional Form Styling - inherited from create view */
.page-header-card {
  border-radius: 8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  font-size: 0.9rem;
  font-weight: 400;
  line-height: 1.4;
}

.form-card {
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-bottom: 1px solid #e9ecef;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
}

.form-content {
  background-color: #fafbfc;
  min-height: 40vh;
}

.form-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  border-bottom: 2px solid #e9ecef;
  padding-bottom: 0.5rem;
}

.professional-field {
  background-color: white;
}

.professional-field .v-input__control .v-input__slot {
  background-color: white !important;
}

.form-actions {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  border: 1px solid #e9ecef;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.04);
}

.action-button-submit {
  min-width: 200px;
  height: 48px;
  font-weight: 600;
  text-transform: none;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(25, 118, 210, 0.3);
}

.action-button-cancel {
  min-width: 140px;
  height: 48px;
  font-weight: 500;
  text-transform: none;
  border-radius: 6px;
}

.back-button {
  text-transform: none;
  font-weight: 500;
}

/* Disabled field styling */
.v-input--is-disabled .v-input__slot {
  background-color: #f8f9fa !important;
}

.v-input--is-readonly .v-input__slot {
  background-color: #f8f9fa !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-title {
    font-size: 1.25rem;
  }
  
  .page-subtitle {
    font-size: 0.85rem;
  }
  
  .form-section {
    padding: 1rem;
  }
  
  .action-button-submit,
  .action-button-cancel {
    width: 100%;
    margin-bottom: 0.5rem;
  }
}

/* Vuetify component customizations */
.v-chip--outlined {
  border-width: 1.5px;
}

.v-select__selection .v-chip {
  max-height: 28px;
}

.v-autocomplete .v-select__selections .v-chip {
  max-height: 28px;
}
</style>